import * as THREE from 'three'
import type {
  SectionLocalExtents,
  SectionOrientation,
  Vec3,
} from '@viewer/store/sectionSlice'
import { IDENTITY_ORIENTATION } from '@viewer/store/sectionSlice'
import { viewerApi } from '@viewer/lib/viewerApi'
import { resolveWorldBox } from '@viewer/lib/geoTransform'

export type FaceKey =
  | 'minX'
  | 'maxX'
  | 'minY'
  | 'maxY'
  | 'minZ'
  | 'maxZ'

export const FACE_KEYS: FaceKey[] = [
  'minX',
  'maxX',
  'minY',
  'maxY',
  'minZ',
  'maxZ',
]

// Returns the outward normal in OBB local coordinates for a given
// face. The OBB sign + axis pair is encoded in the face key.
export function localFaceNormal(face: FaceKey): Vec3 {
  switch (face) {
    case 'minX':
      return [-1, 0, 0]
    case 'maxX':
      return [1, 0, 0]
    case 'minY':
      return [0, -1, 0]
    case 'maxY':
      return [0, 1, 0]
    case 'minZ':
      return [0, 0, -1]
    case 'maxZ':
      return [0, 0, 1]
  }
}

// Convert a local-frame vector to world space.
export function localToWorldDir(o: SectionOrientation, v: Vec3): THREE.Vector3 {
  return new THREE.Vector3(
    v[0] * o.axisX[0] + v[1] * o.axisY[0] + v[2] * o.axisZ[0],
    v[0] * o.axisX[1] + v[1] * o.axisY[1] + v[2] * o.axisZ[1],
    v[0] * o.axisX[2] + v[1] * o.axisY[2] + v[2] * o.axisZ[2],
  )
}

export function localToWorldPoint(
  o: SectionOrientation,
  local: Vec3,
): THREE.Vector3 {
  const d = localToWorldDir(o, local)
  return d.add(new THREE.Vector3(o.origin[0], o.origin[1], o.origin[2]))
}

// World vector → local-frame vector (assumes axes are orthonormal).
export function worldToLocalPoint(
  o: SectionOrientation,
  world: { x: number; y: number; z: number },
): Vec3 {
  const dx = world.x - o.origin[0]
  const dy = world.y - o.origin[1]
  const dz = world.z - o.origin[2]
  return [
    dx * o.axisX[0] + dy * o.axisX[1] + dz * o.axisX[2],
    dx * o.axisY[0] + dy * o.axisY[1] + dz * o.axisY[2],
    dx * o.axisZ[0] + dy * o.axisZ[1] + dz * o.axisZ[2],
  ]
}

// 8 corners of the OBB in world space (order matches standard
// box wireframes; see EDGES constant in the overlay).
export function obbCorners(
  o: SectionOrientation,
  e: SectionLocalExtents,
): THREE.Vector3[] {
  const xs: [number, number] = [e.minX, e.maxX]
  const ys: [number, number] = [e.minY, e.maxY]
  const zs: [number, number] = [e.minZ, e.maxZ]
  const out: THREE.Vector3[] = []
  for (let zi = 0; zi < 2; zi++) {
    for (let yi = 0; yi < 2; yi++) {
      for (let xi = 0; xi < 2; xi++) {
        out.push(localToWorldPoint(o, [xs[xi], ys[yi], zs[zi]]))
      }
    }
  }
  return out
}

// Local-space centre of a given face.
export function localFaceCenter(
  e: SectionLocalExtents,
  face: FaceKey,
): Vec3 {
  const cx = (e.minX + e.maxX) / 2
  const cy = (e.minY + e.maxY) / 2
  const cz = (e.minZ + e.maxZ) / 2
  switch (face) {
    case 'minX':
      return [e.minX, cy, cz]
    case 'maxX':
      return [e.maxX, cy, cz]
    case 'minY':
      return [cx, e.minY, cz]
    case 'maxY':
      return [cx, e.maxY, cz]
    case 'minZ':
      return [cx, cy, e.minZ]
    case 'maxZ':
      return [cx, cy, e.maxZ]
  }
}

// Compute the AABB of all visible models in WORLD coords. Used to
// seed initial section box extents and to refit after orientation
// changes. Federated models with different map origins each land at
// their own world position, and the section box must follow.
//
// O `model.box` do Fragments 3.4.3 já vem em coordenadas do mundo
// (`_bbox.applyMatrix4(object.matrixWorld)`). Aplicar a matriz outra
// vez, como se fazia aqui, punha a caixa ao lado do edifício sempre
// que o modelo tinha deslocamento (no exemplo, -38, 3.5, -97): a caixa
// nascia longe, cortava o modelo todo e ficava fora do enquadramento.
// O resolveWorldBox trata da ambiguidade e, com a cache primada, dá a
// extensão real da geometria em vez da caixa inflada do motor.
export function getModelsBbox(): THREE.Box3 | null {
  const world = viewerApi.getWorld()
  if (!world) return null
  const box = new THREE.Box3()
  let initialised = false
  for (const [, model] of viewerApi.iterModels()) {
    if (!(model.object as THREE.Object3D).visible) continue
    const mb = resolveWorldBox(model)
    if (!mb || mb.isEmpty()) continue
    if (!initialised) {
      box.copy(mb)
      initialised = true
    } else box.union(mb)
  }
  return initialised ? box : null
}

// Initial extents for the default axis-aligned section box: the
// model's world bbox itself (with identity orientation, world coords
// match local coords).
export function defaultExtents(box: THREE.Box3): SectionLocalExtents {
  return {
    minX: box.min.x,
    maxX: box.max.x,
    minY: box.min.y,
    maxY: box.max.y,
    minZ: box.min.z,
    maxZ: box.max.z,
  }
}

// Project the 8 corners of an arbitrary world-space AABB into the
// local frame of the given orientation, returning the AABB-of-the-
// projection in local coords. Used to compute extents that contain
// the full model after rotating to align with a face.
export function projectAabbToLocal(
  o: SectionOrientation,
  box: THREE.Box3,
): SectionLocalExtents {
  const corners: { x: number; y: number; z: number }[] = [
    { x: box.min.x, y: box.min.y, z: box.min.z },
    { x: box.max.x, y: box.min.y, z: box.min.z },
    { x: box.min.x, y: box.max.y, z: box.min.z },
    { x: box.max.x, y: box.max.y, z: box.min.z },
    { x: box.min.x, y: box.min.y, z: box.max.z },
    { x: box.max.x, y: box.min.y, z: box.max.z },
    { x: box.min.x, y: box.max.y, z: box.max.z },
    { x: box.max.x, y: box.max.y, z: box.max.z },
  ]
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity
  for (const c of corners) {
    const [lx, ly, lz] = worldToLocalPoint(o, c)
    if (lx < minX) minX = lx
    if (ly < minY) minY = ly
    if (lz < minZ) minZ = lz
    if (lx > maxX) maxX = lx
    if (ly > maxY) maxY = ly
    if (lz > maxZ) maxZ = lz
  }
  return { minX, maxX, minY, maxY, minZ, maxZ }
}

// Build an OBB orientation from a picked face: axisZ aligns with the
// face normal; axisX is the world Y reference orthogonalised; axisY
// is the cross product. Origin snaps to the picked point. Mirrors
// the BCF viewer's Gram-Schmidt construction.
export function orientationFromFace(
  point: { x: number; y: number; z: number },
  normal: { x: number; y: number; z: number },
): SectionOrientation | null {
  const nlen = Math.hypot(normal.x, normal.y, normal.z)
  if (nlen < 1e-6) return null
  const Nx = normal.x / nlen
  const Ny = normal.y / nlen
  const Nz = normal.z / nlen
  // Gimbal-lock guard: if the normal is nearly vertical (within ~10°
  // of world Y), use world X as reference instead of world Y.
  const ref =
    Math.abs(Ny) < 0.985 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 }
  const dot = Nx * ref.x + Ny * ref.y + Nz * ref.z
  let axx = ref.x - Nx * dot
  let axy = ref.y - Ny * dot
  let axz = ref.z - Nz * dot
  const axlen = Math.hypot(axx, axy, axz)
  if (axlen < 1e-6) return null
  axx /= axlen
  axy /= axlen
  axz /= axlen
  // axisY = N × axisX (right-handed)
  const ayx = Ny * axz - Nz * axy
  const ayy = Nz * axx - Nx * axz
  const ayz = Nx * axy - Ny * axx
  return {
    origin: [point.x, point.y, point.z],
    axisX: [axx, axy, axz],
    axisY: [ayx, ayy, ayz],
    axisZ: [Nx, Ny, Nz],
  }
}

// Convenience to know if the orientation is the identity (world
// axes), useful for some UI hints.
export function isIdentityOrientation(o: SectionOrientation): boolean {
  return (
    o.axisX[0] === IDENTITY_ORIENTATION.axisX[0] &&
    o.axisX[1] === IDENTITY_ORIENTATION.axisX[1] &&
    o.axisX[2] === IDENTITY_ORIENTATION.axisX[2] &&
    o.axisY[0] === IDENTITY_ORIENTATION.axisY[0] &&
    o.axisY[1] === IDENTITY_ORIENTATION.axisY[1] &&
    o.axisY[2] === IDENTITY_ORIENTATION.axisY[2] &&
    o.axisZ[0] === IDENTITY_ORIENTATION.axisZ[0] &&
    o.axisZ[1] === IDENTITY_ORIENTATION.axisZ[1] &&
    o.axisZ[2] === IDENTITY_ORIENTATION.axisZ[2] &&
    o.origin[0] === 0 &&
    o.origin[1] === 0 &&
    o.origin[2] === 0
  )
}
